import { createContext, useContext, type ReactNode } from 'react';
import type { FeatureFlagMap, FeatureFlagName } from './flags.config';
import { DEFAULT_FEATURE_FLAGS } from './flags.config';

const FeatureFlagContext = createContext<FeatureFlagMap>(DEFAULT_FEATURE_FLAGS);

export interface FeatureFlagProviderProps {
  flags: FeatureFlagMap;
  children: ReactNode;
}

/**
 * React provider that exposes resolved feature flags to the component tree.
 * The provider itself does not decide flag values — it receives them already
 * resolved from a FlagProvider instance (env-based in this phase).
 */
export function FeatureFlagProvider({ flags, children }: FeatureFlagProviderProps) {
  return <FeatureFlagContext.Provider value={flags}>{children}</FeatureFlagContext.Provider>;
}

/**
 * Runtime check hook per docs/architecture/ARCHITECTURE-V2-ADDENDUM.md (TASK 5).
 * Usage: `if (useFeatureFlag('gallery')) { ... }`
 */
export function useFeatureFlag(name: FeatureFlagName): boolean {
  const flags = useContext(FeatureFlagContext);
  return flags[name];
}
